// src/clients/AuditLogClient.ts
var AuditLogClient = class {
  constructor(transport) {
    this.transport = transport;
  }
  transport;
  async registrarEvento(req) {
    return this.transport.post(
      "/api/v1/audit/eventos",
      req,
      { idempotent: true }
    );
  }
  async listarEventos(params = {}) {
    const qs = {};
    if (params.desde !== void 0) qs.desde = params.desde;
    if (params.hasta !== void 0) qs.hasta = params.hasta;
    if (params.tipo !== void 0) qs.tipo = params.tipo;
    if (params.id_usuario !== void 0) qs.id_usuario = params.id_usuario;
    if (params.entidad !== void 0) qs.entidad = params.entidad;
    if (params.rowid !== void 0) qs.rowid = params.rowid;
    if (params.limit !== void 0) qs.limit = params.limit;
    if (params.cursor !== void 0) qs.cursor = params.cursor;
    return this.transport.get("/api/v1/audit/eventos", {
      params: qs
    });
  }
  async verificarEvento(idEvento) {
    return this.transport.get(
      `/api/v1/audit/eventos/${encodeURIComponent(idEvento)}/verificar`
    );
  }
  async exportar(req) {
    return this.transport.post(
      "/api/v1/audit/export",
      req,
      { idempotent: true }
    );
  }
};

// src/clients/AuthenticationClient.ts
var AuthenticationClient = class {
  constructor(transport) {
    this.transport = transport;
  }
  transport;
  async login(req) {
    return this.transport.post("/api/v1/auth/login", req, {
      anonymous: true,
      // `login` no usa Idempotency-Key (cada intento es un evento distinto).
      idempotent: false
    });
  }
  async refresh(req) {
    return this.transport.post("/api/v1/auth/refresh", req, {
      anonymous: true,
      idempotent: false
    });
  }
  async logout(req) {
    return this.transport.post("/api/v1/auth/logout", req, {
      idempotent: true
    });
  }
  async me() {
    return this.transport.get("/api/v1/auth/me");
  }
  async changePassword(req) {
    return this.transport.post(
      "/api/v1/auth/change-password",
      req,
      { idempotent: false }
    );
  }
  async changeEnterprise(req) {
    return this.transport.post(
      "/api/v1/auth/cambiar-empresa",
      req,
      { idempotent: true }
    );
  }
};

// src/clients/BusinessRulesClient.ts
var BusinessRulesClient = class {
  constructor(transport) {
    this.transport = transport;
  }
  transport;
  async validarPedido(req) {
    return this.transport.post(
      "/api/v1/rules/pedidos/validar",
      req,
      { idempotent: true }
    );
  }
  async aplicarCupon(req) {
    return this.transport.post(
      "/api/v1/rules/cupones/aplicar",
      req
    );
  }
  async reservarStock(req) {
    return this.transport.post(
      "/api/v1/rules/stock/reservar",
      req,
      { idempotent: true, idempotencyKey: req.ID_PEDIDO_TEMP }
    );
  }
  async aprobarPedido(req) {
    return this.transport.post(
      "/api/v1/rules/pedidos/aprobar",
      req,
      { idempotent: true }
    );
  }
  async scoringCliente(req) {
    return this.transport.post(
      "/api/v1/rules/clientes/scoring",
      req
    );
  }
  /**
   * Endpoint generico para reglas configurables por proyecto
   * (`/api/v1/rules/comisiones/calcular`, `/precios/calcular`, etc.).
   */
  async invoke(ruta, req) {
    const clean = ruta.replace(/^\/+/, "");
    return this.transport.post(`/api/v1/rules/${clean}`, req, {
      idempotent: true
    });
  }
};

// src/clients/CollectionClient.ts
var CollectionClient = class {
  constructor(transport, projectId = null) {
    this.transport = transport;
    this.projectId = projectId;
  }
  transport;
  projectId;
  /**
   * Devuelve el contrato legal para esta colección en el contexto del usuario actual
   * (whitelist de campos filtrables/ordenables/proyectables y operadores admitidos).
   * El cliente lo usa para construir UI dinámica y validar formularios antes de
   * enviar al servidor — **no como garantía de seguridad** (el backend revalida).
   */
  async describe(collection) {
    return this.transport.get(
      `/coll/${encodeURIComponent(collection)}/_describe`,
      this.headerOpts()
    );
  }
  /**
   * Lista paginada con DSL. Para paginación pasa `dsl.page.cursor` recibido en
   * la respuesta anterior (`NEXT_CURSOR`). Si `dsl` es nulo, el backend usa los
   * defaults del descriptor.
   */
  async list(collection, dsl) {
    return this.transport.post(
      `/coll/${encodeURIComponent(collection)}/_list`,
      dsl ?? {},
      this.headerOpts()
    );
  }
  async count(collection, filter) {
    return this.transport.post(
      `/coll/${encodeURIComponent(collection)}/_count`,
      { filter },
      this.headerOpts()
    );
  }
  /** Devuelve una fila por ROWID. 404 si no existe (para este tenant). */
  async get(collection, rowid) {
    if (!/^[A-Fa-f0-9]{32}$/.test(rowid)) {
      throw new Error(`ROWID inv\xE1lido (esperado 32 hex): ${rowid}`);
    }
    return this.transport.get(
      `/coll/${encodeURIComponent(collection)}/${rowid}`,
      this.headerOpts()
    );
  }
  /** Crea una fila. Devuelve el ROWID generado por el servidor. */
  async create(collection, row) {
    return this.transport.post(
      `/coll/${encodeURIComponent(collection)}`,
      { ROW: row },
      { idempotent: true, ...this.headerOpts() }
    );
  }
  /** Actualiza parcialmente una fila por ROWID. */
  async update(collection, rowid, row) {
    if (!/^[A-Fa-f0-9]{32}$/.test(rowid)) {
      throw new Error(`ROWID inv\xE1lido (esperado 32 hex): ${rowid}`);
    }
    await this.transport.patch(
      `/coll/${encodeURIComponent(collection)}/${rowid}`,
      { ROW: row },
      this.headerOpts()
    );
  }
  async remove(collection, rowid) {
    if (!/^[A-Fa-f0-9]{32}$/.test(rowid)) {
      throw new Error(`ROWID inv\xE1lido (esperado 32 hex): ${rowid}`);
    }
    await this.transport.delete(
      `/coll/${encodeURIComponent(collection)}/${rowid}`,
      null,
      this.headerOpts()
    );
  }
  /**
   * Inyecta `X-Xone-Project` si el cliente fue construido con `projectId`. Backend
   * acepta también claim JWT `project_id` o config default; este header es
   * fallback útil para escenarios donde el JWT no incluye el claim.
   */
  headerOpts() {
    if (!this.projectId) return {};
    return { extra: { "X-Xone-Project": this.projectId } };
  }
};

// src/clients/CryptoClient.ts
var CryptoClient = class {
  constructor(transport) {
    this.transport = transport;
  }
  transport;
  async encrypt(req) {
    return this.transport.post(
      "/api/v1/crypto/encrypt",
      req
    );
  }
  async decrypt(req) {
    return this.transport.post(
      "/api/v1/crypto/decrypt",
      req
    );
  }
  async sign(req) {
    return this.transport.post(
      "/api/v1/crypto/sign",
      req,
      { idempotent: true }
    );
  }
  async verify(req) {
    return this.transport.post(
      "/api/v1/crypto/verify",
      req
    );
  }
  async validateCertificate(req) {
    return this.transport.post(
      "/api/v1/crypto/cert/validate",
      req
    );
  }
  async hash(req) {
    return this.transport.post(
      "/api/v1/crypto/hash",
      req
    );
  }
};

// src/clients/DataQueryClient.ts
var DataQueryClient = class {
  constructor(transport) {
    this.transport = transport;
  }
  transport;
  async catalog() {
    return this.transport.get("/query/catalogo");
  }
  async execute(nombre, req) {
    return this.transport.post(
      `/query/${encodeURIComponent(nombre)}`,
      req
    );
  }
  async export(nombre, req) {
    return this.transport.post(
      `/query/${encodeURIComponent(nombre)}/export`,
      req,
      { idempotent: true }
    );
  }
};

// src/clients/DataReplicaClient.ts
var DataReplicaClient = class {
  constructor(transport) {
    this.transport = transport;
  }
  transport;
  async iniciar(req) {
    return this.transport.post(
      "/api/v1/replica/iniciar",
      req,
      { idempotent: true }
    );
  }
  async uploadBatch(sesionId, req) {
    return this.transport.post(
      `/api/v1/replica/${encodeURIComponent(sesionId)}/upload`,
      req,
      { idempotent: true }
    );
  }
  async download(sesionId, params) {
    const qs = {
      coleccion: params.coleccion
    };
    if (params.desde !== void 0) qs.desde = params.desde;
    if (params.cursor !== void 0 && params.cursor !== null) qs.cursor = params.cursor;
    if (params.limit !== void 0) qs.limit = params.limit;
    return this.transport.get(
      `/api/v1/replica/${encodeURIComponent(sesionId)}/download`,
      { params: qs }
    );
  }
  async finalizar(sesionId, req) {
    return this.transport.post(
      `/api/v1/replica/${encodeURIComponent(sesionId)}/finalizar`,
      req
    );
  }
  async resolveConflict(sesionId, req) {
    return this.transport.post(
      `/api/v1/replica/${encodeURIComponent(sesionId)}/conflictos/resolver`,
      req,
      { idempotent: true }
    );
  }
};

// src/clients/DeviceRegistryClient.ts
var DeviceRegistryClient = class {
  constructor(transport) {
    this.transport = transport;
  }
  transport;
  /**
   * Si el `DEVICE_ID` ya pertenece a otro usuario, anadir
   * `confirmOwnerChange=true` para emitir el header
   * `X-Confirm-Device-Owner-Change: true` (plan 12 §3.1 regla 2).
   */
  async register(req, confirmOwnerChange = false) {
    return this.transport.post(
      "/api/v1/devices/registrar",
      req,
      {
        idempotent: true,
        idempotencyKey: req.DEVICE_ID,
        extra: confirmOwnerChange ? { "X-Confirm-Device-Owner-Change": "true" } : {}
      }
    );
  }
  async heartbeat(req) {
    return this.transport.post(
      "/api/v1/devices/heartbeat",
      req
    );
  }
  async me() {
    return this.transport.get("/api/v1/devices/me");
  }
  async list() {
    return this.transport.get("/api/v1/devices");
  }
  async revoke(id) {
    return this.transport.post(
      `/api/v1/devices/${encodeURIComponent(id)}/revocar`,
      {},
      { idempotent: true }
    );
  }
};

// src/clients/FileStorageClient.ts
import { XoneHttpError } from "@xone-web/http";

// src/errors/XoneBackendError.ts
var XoneBackendError = class _XoneBackendError extends Error {
  code;
  status;
  problem;
  body;
  constructor(code, status, problem, body) {
    super(buildMessage(code, status, problem));
    this.name = "XoneBackendError";
    this.code = code;
    this.status = status;
    this.problem = problem;
    this.body = body;
    Object.setPrototypeOf(this, _XoneBackendError.prototype);
  }
};
function buildMessage(code, status, problem) {
  const head = `XoneBackendError(${code}, status=${status})`;
  const detail = problem.detail ?? problem.title;
  return detail ? `${head}: ${detail}` : head;
}

// src/types/errorCodes.ts
var XONE_ERROR_CODES = {
  // --- Auth ----------------------------------------------------------------
  AUTH_INVALID_CREDENTIALS: "XONE.AUTH.INVALID_CREDENTIALS",
  AUTH_USER_LOCKED: "XONE.AUTH.USER_LOCKED",
  AUTH_ENTERPRISE_FORBIDDEN: "XONE.AUTH.ENTERPRISE_FORBIDDEN",
  AUTH_REFRESH_INVALID: "XONE.AUTH.REFRESH_INVALID",
  AUTH_DEVICE_MISMATCH: "XONE.AUTH.DEVICE_MISMATCH",
  AUTH_REFRESH_REUSE: "XONE.AUTH.REFRESH_REUSE",
  AUTH_PASSWORD_POLICY: "XONE.AUTH.PASSWORD_POLICY",
  // --- Session -------------------------------------------------------------
  SESSION_MACRO_NOT_ALLOWED: "XONE.SESSION.MACRO_NOT_ALLOWED",
  SESSION_VERSION_OUTDATED: "XONE.SESSION.VERSION_OUTDATED",
  // --- Replica -------------------------------------------------------------
  REPLICA_SESSION_ACTIVE: "XONE.REPLICA.SESSION_ACTIVE",
  REPLICA_CONFLICT: "XONE.REPLICA.CONFLICT",
  REPLICA_CURSOR_INVALID: "XONE.REPLICA.CURSOR_INVALID",
  // --- DataQuery -----------------------------------------------------------
  QUERY_NOT_FOUND: "XONE.QUERY.NOT_FOUND",
  QUERY_PERMISSION_DENIED: "XONE.QUERY.PERMISSION_DENIED",
  QUERY_TIMEOUT: "XONE.QUERY.TIMEOUT",
  // --- Rules ---------------------------------------------------------------
  RULES_STOCK_INSUFICIENTE: "XONE.RULES.STOCK_INSUFICIENTE",
  RULES_CUPON_INVALIDO: "XONE.RULES.CUPON_INVALIDO",
  RULES_CREDITO_INSUFICIENTE: "XONE.RULES.CREDITO_INSUFICIENTE",
  RULES_DESCUENTO_NO_AUTORIZADO: "XONE.RULES.DESCUENTO_NO_AUTORIZADO",
  // --- Audit ---------------------------------------------------------------
  AUDIT_FORBIDDEN: "XONE.AUDIT.FORBIDDEN",
  AUDIT_CHAIN_BROKEN: "XONE.AUDIT.CHAIN_BROKEN",
  // --- Push ----------------------------------------------------------------
  PUSH_TOKEN_INVALID: "XONE.PUSH.TOKEN_INVALID",
  // --- Messaging -----------------------------------------------------------
  MESSAGING_TEMPLATE_NOT_FOUND: "XONE.MESSAGING.TEMPLATE_NOT_FOUND",
  MESSAGING_RATE_LIMIT: "XONE.MESSAGING.RATE_LIMIT",
  // --- Crypto --------------------------------------------------------------
  CRYPTO_KEY_NOT_FOUND: "XONE.CRYPTO.KEY_NOT_FOUND",
  CRYPTO_AAD_MISMATCH: "XONE.CRYPTO.AAD_MISMATCH",
  // --- Files ---------------------------------------------------------------
  FILES_TOO_LARGE: "XONE.FILES.TOO_LARGE",
  FILES_HASH_MISMATCH: "XONE.FILES.HASH_MISMATCH",
  FILES_AV_INFECTED: "XONE.FILES.AV_INFECTED",
  // --- Devices -------------------------------------------------------------
  DEVICES_DEVICE_ID_INVALID: "XONE.DEVICES.DEVICE_ID_INVALID",
  DEVICES_CUOTA_EXCEDIDA: "XONE.DEVICES.CUOTA_EXCEDIDA",
  DEVICES_BLOQUEADO: "XONE.DEVICES.BLOQUEADO",
  // --- Backend (transversales) --------------------------------------------
  BACKEND_NOT_IMPLEMENTED: "XONE.BACKEND.NOT_IMPLEMENTED",
  BACKEND_INVALID_RESPONSE: "XONE.BACKEND.INVALID_RESPONSE",
  BACKEND_NETWORK: "XONE.BACKEND.NETWORK"
};

// src/helpers/problemDetails.ts
function parseProblemDetails(status, body) {
  let parsed = null;
  try {
    const raw = JSON.parse(body);
    if (raw && typeof raw === "object" && !Array.isArray(raw)) {
      parsed = raw;
    }
  } catch {
    parsed = null;
  }
  const code = pickString(parsed, "code") ?? deriveCodeFromStatus(status);
  const title = pickString(parsed, "title") ?? defaultTitle(status);
  const type = pickString(parsed, "type") ?? "about:blank";
  const detail = pickString(parsed, "detail") ?? void 0;
  const traceId = pickString(parsed, "traceId") ?? void 0;
  const instance = pickString(parsed, "instance") ?? void 0;
  const errors = Array.isArray(parsed?.errors) ? parsed.errors : void 0;
  const result = {
    type,
    title,
    status,
    code,
    ...detail ? { detail } : {},
    ...traceId ? { traceId } : {},
    ...instance ? { instance } : {},
    ...errors ? { errors } : {}
  };
  if (parsed) {
    for (const [k, v] of Object.entries(parsed)) {
      if (k in result) continue;
      result[k] = v;
    }
  }
  return result;
}
function pickString(obj, key) {
  if (!obj) return null;
  const v = obj[key];
  return typeof v === "string" ? v : null;
}
function defaultTitle(status) {
  if (status === 0) return "Network error";
  if (status === 401) return "Unauthorized";
  if (status === 403) return "Forbidden";
  if (status === 404) return "Not Found";
  if (status === 409) return "Conflict";
  if (status === 422) return "Unprocessable Entity";
  if (status >= 400 && status < 500) return "Client Error";
  if (status >= 500) return "Server Error";
  return "Error";
}
function deriveCodeFromStatus(status) {
  if (status === 0 || status === -1) return XONE_ERROR_CODES.BACKEND_NETWORK;
  if (status === 401) return XONE_ERROR_CODES.AUTH_INVALID_CREDENTIALS;
  if (status === 403) return "XONE.BACKEND.FORBIDDEN";
  if (status === 404) return "XONE.BACKEND.NOT_FOUND";
  if (status === 409) return "XONE.BACKEND.CONFLICT";
  if (status === 422) return "XONE.BACKEND.VALIDATION";
  if (status === 423) return XONE_ERROR_CODES.AUTH_USER_LOCKED;
  if (status === 429) return "XONE.BACKEND.RATE_LIMIT";
  if (status >= 500) return "XONE.BACKEND.SERVER_ERROR";
  return "XONE.BACKEND.UNKNOWN";
}

// src/clients/FileStorageClient.ts
var FileStorageClient = class {
  constructor(transport, $http) {
    this.transport = transport;
    this.$http = $http;
  }
  transport;
  $http;
  async reserve(meta) {
    return this.transport.post(
      "/api/v1/files/uploads",
      meta,
      { idempotent: true, idempotencyKey: meta.HASH_SHA256 }
    );
  }
  /**
   * Sube los bytes directamente al storage (Azure Blob) usando la URL
   * presignada devuelta por `reserve`.
   *
   * Nota: no inyecta `Authorization` — la firma de la URL es la autorizacion.
   * Los `headers` adicionales (e.g. `x-ms-blob-type: BlockBlob`) deben
   * pasarse en `requiredHeaders` (provienen de `FileReservation.HEADERS_REQUERIDOS`).
   */
  async upload(uploadUrl, bytes, mimeType, requiredHeaders = {}) {
    const headers = {
      "Content-Type": mimeType,
      ...requiredHeaders
    };
    try {
      const data = bytes instanceof Blob ? bytes : bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
      await this.$http.put(uploadUrl, { headers, data });
    } catch (err) {
      if (err instanceof XoneHttpError) {
        const status = err.status > 0 ? err.status : 0;
        const problem = parseProblemDetails(status, err.body);
        throw new XoneBackendError(problem.code, status, problem, err.body);
      }
      if (err instanceof Error) {
        throw new XoneBackendError(
          XONE_ERROR_CODES.BACKEND_NETWORK,
          0,
          {
            type: "about:blank",
            title: "Upload network error",
            status: 0,
            code: XONE_ERROR_CODES.BACKEND_NETWORK,
            detail: err.message
          },
          err.message
        );
      }
      throw err;
    }
  }
  async confirm(fileId, hashSha256) {
    const body = { HASH_SHA256: hashSha256 };
    return this.transport.post(
      `/api/v1/files/${encodeURIComponent(fileId)}/confirm`,
      body,
      { idempotent: true, idempotencyKey: hashSha256 }
    );
  }
  /**
   * Resuelve la URL de descarga (el endpoint devuelve `302` a una URL
   * presignada del storage). La descarga binaria real se delega al
   * llamador para no acoplar OPFS aqui — `@xone-web/storage` se encarga.
   */
  async getDownloadInfo(fileId) {
    return this.transport.get(
      `/api/v1/files/${encodeURIComponent(fileId)}/download`
    );
  }
  async delete(fileId) {
    return this.transport.delete(
      `/api/v1/files/${encodeURIComponent(fileId)}`,
      null,
      { idempotent: true }
    );
  }
};

// src/clients/MessagingClient.ts
var MessagingClient = class {
  constructor(transport) {
    this.transport = transport;
  }
  transport;
  async sendEmail(req) {
    return this.transport.post(
      "/api/v1/messaging/email",
      req,
      { idempotent: true }
    );
  }
  async sendSms(req) {
    return this.transport.post(
      "/api/v1/messaging/sms",
      req,
      { idempotent: true }
    );
  }
  async listTemplates() {
    return this.transport.get(
      "/api/v1/messaging/plantillas"
    );
  }
  async getMessage(id) {
    return this.transport.get(
      `/api/v1/messaging/mensajes/${encodeURIComponent(id)}`
    );
  }
};

// src/clients/PushNotificationClient.ts
var PushNotificationClient = class {
  constructor(transport) {
    this.transport = transport;
  }
  transport;
  async register(req) {
    return this.transport.post(
      "/api/v1/push/registrar",
      req,
      { idempotent: true }
    );
  }
  async unregister(req = {}) {
    return this.transport.delete(
      "/api/v1/push/registrar",
      req
    );
  }
  /**
   * Endpoint interno (`/api/v1/push/enviar`). Expuesto aqui por completitud
   * — solo es invocable con un token de servicio; el cliente XOne normal
   * recibira `403` si lo intenta.
   */
  async send(req) {
    return this.transport.post(
      "/api/v1/push/enviar",
      req,
      { idempotent: true }
    );
  }
  async historial(params = {}) {
    const qs = {};
    if (params.desde !== void 0) qs.desde = params.desde;
    if (params.hasta !== void 0) qs.hasta = params.hasta;
    if (params.limit !== void 0) qs.limit = params.limit;
    if (params.cursor !== void 0) qs.cursor = params.cursor;
    return this.transport.get("/api/v1/push/historial", {
      params: qs
    });
  }
};

// src/clients/SessionMacroClient.ts
var SessionMacroClient = class {
  constructor(transport) {
    this.transport = transport;
  }
  transport;
  async getMacros(keys) {
    const params = keys && keys.length > 0 ? { keys: keys.join(",") } : void 0;
    return this.transport.get(
      "/api/v1/session/macros",
      params ? { params } : {}
    );
  }
  async changeEnterprise(req) {
    return this.transport.post(
      "/api/v1/session/macros/cambiar-empresa",
      req,
      { idempotent: true }
    );
  }
  async updatePreferences(req) {
    return this.transport.put(
      "/api/v1/session/preferencias",
      req,
      { idempotent: true }
    );
  }
  async validateMacro(req) {
    return this.transport.post(
      "/api/v1/session/validar-macro",
      req
    );
  }
};

// src/clients/WebRtcSignalingClient.ts
var WebRtcSignalingClient = class {
  async createSession(_payload) {
    throw this.notImplemented("createSession");
  }
  async offer(_sessionId, _sdp) {
    throw this.notImplemented("offer");
  }
  async answer(_sessionId, _sdp) {
    throw this.notImplemented("answer");
  }
  async setRemoteSdp(_sessionId, _sdp) {
    throw this.notImplemented("setRemoteSdp");
  }
  async setRemoteIceCandidate(_sessionId, _candidate) {
    throw this.notImplemented("setRemoteIceCandidate");
  }
  notImplemented(method) {
    return new XoneBackendError(
      XONE_ERROR_CODES.BACKEND_NOT_IMPLEMENTED,
      501,
      {
        type: "about:blank",
        title: "WebRtcSignalingService no implementado",
        status: 501,
        code: XONE_ERROR_CODES.BACKEND_NOT_IMPLEMENTED,
        detail: `WebRtcSignalingService.${method}() esta en backlog y no forma parte del MVP. Ver plan backend 11.`
      },
      ""
    );
  }
};

// src/BackendClient.ts
function assembleClients(transport, $http, projectId = null) {
  return {
    auth: new AuthenticationClient(transport),
    session: new SessionMacroClient(transport),
    replica: new DataReplicaClient(transport),
    dataQuery: new DataQueryClient(transport),
    collection: new CollectionClient(transport, projectId),
    rules: new BusinessRulesClient(transport),
    audit: new AuditLogClient(transport),
    push: new PushNotificationClient(transport),
    messaging: new MessagingClient(transport),
    crypto: new CryptoClient(transport),
    files: new FileStorageClient(transport, $http),
    devices: new DeviceRegistryClient(transport),
    rtc: new WebRtcSignalingClient()
  };
}

// src/helpers/transport.ts
import { XoneHttpError as XoneHttpError2 } from "@xone-web/http";

// src/helpers/headers.ts
function generateRowId() {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") {
    return c.randomUUID().replace(/-/g, "");
  }
  let out = "";
  for (let i = 0; i < 32; i++) {
    out += Math.floor(Math.random() * 16).toString(16);
  }
  return out;
}
function buildHeaders(opts = {}) {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json"
  };
  if (opts.idempotent) {
    headers["Idempotency-Key"] = opts.idempotencyKey ?? generateRowId();
  } else if (opts.idempotencyKey) {
    headers["Idempotency-Key"] = opts.idempotencyKey;
  }
  headers["X-Correlation-Id"] = generateRowId();
  const macros = opts.macros ?? null;
  if (macros) {
    const deviceId = macros.getGlobalMacro("DEVICE_ID");
    if (deviceId) headers["X-Device-Id"] = deviceId;
    const locale = macros.getGlobalMacro("LOCALE") ?? macros.getGlobalMacro("IDIOMA");
    if (locale) headers["Accept-Language"] = locale;
  }
  headers["X-Xone-Client"] = "@xone-web/backend-client";
  if (opts.extra) {
    for (const [k, v] of Object.entries(opts.extra)) {
      headers[k] = v;
    }
  }
  if (opts.anonymous) {
    headers["X-Xone-Anonymous"] = "1";
  }
  return headers;
}

// src/helpers/transport.ts
var BackendTransport = class {
  apiBase;
  $http;
  macros;
  autoIdempotency;
  constructor(config) {
    this.apiBase = config.apiBase.replace(/\/+$/, "");
    this.$http = config.$http;
    this.macros = config.macros;
    this.autoIdempotency = config.autoIdempotency;
  }
  /** Construye la URL absoluta. `path` se asume con `/` al inicio. */
  buildUrl(path, params) {
    const base = path.startsWith("/") ? path : `/${path}`;
    const url = `${this.apiBase}${base}`;
    if (!params) return url;
    const qs = [];
    for (const [k, v] of Object.entries(params)) {
      if (v === void 0 || v === null) continue;
      qs.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
    }
    return qs.length === 0 ? url : `${url}?${qs.join("&")}`;
  }
  async get(path, opts = {}) {
    const url = this.buildUrl(path, opts.params);
    const headers = buildHeaders({ ...opts, macros: opts.macros ?? this.macros });
    try {
      const resp = await this.$http.get(url, { headers });
      return this.parseBody(resp);
    } catch (err) {
      throw this.toBackendError(err);
    }
  }
  async post(path, body, opts = {}) {
    return this.send("POST", path, body, opts);
  }
  async put(path, body, opts = {}) {
    return this.send("PUT", path, body, opts);
  }
  async patch(path, body, opts = {}) {
    return this.send("PATCH", path, body, opts);
  }
  async delete(path, body = null, opts = {}) {
    return this.send("DELETE", path, body, opts);
  }
  async send(method, path, body, opts) {
    const url = this.buildUrl(path, opts.params);
    const headers = buildHeaders({
      ...opts,
      macros: opts.macros ?? this.macros,
      idempotent: opts.idempotent ?? this.autoIdempotency
    });
    const data = body === void 0 || body === null ? "" : typeof body === "string" ? body : JSON.stringify(body);
    try {
      let resp;
      switch (method) {
        case "POST":
          resp = await this.$http.post(url, { headers, data });
          break;
        case "PUT":
          resp = await this.$http.put(url, { headers, data });
          break;
        case "PATCH":
          resp = await this.$http.patch(url, { headers, data });
          break;
        case "DELETE":
          resp = await this.$http.delete(url, { headers, data });
          break;
      }
      return this.parseBody(resp);
    } catch (err) {
      throw this.toBackendError(err);
    }
  }
  parseBody(resp) {
    if (resp.status === 204 || resp.data === "") {
      return void 0;
    }
    if (resp.json !== null) {
      return resp.json;
    }
    try {
      return JSON.parse(resp.data);
    } catch {
      throw new XoneBackendError(
        XONE_ERROR_CODES.BACKEND_INVALID_RESPONSE,
        resp.status,
        {
          type: "about:blank",
          title: "Invalid response body",
          status: resp.status,
          code: XONE_ERROR_CODES.BACKEND_INVALID_RESPONSE,
          detail: "El backend devolvio una respuesta que no es JSON valido."
        },
        resp.data
      );
    }
  }
  toBackendError(err) {
    if (err instanceof XoneBackendError) return err;
    if (err instanceof XoneHttpError2) {
      const status = err.status > 0 ? err.status : 0;
      const problem = parseProblemDetails(status, err.body);
      return new XoneBackendError(problem.code, status, problem, err.body);
    }
    if (err instanceof Error) {
      const problem = parseProblemDetails(0, err.message);
      return new XoneBackendError(
        XONE_ERROR_CODES.BACKEND_NETWORK,
        0,
        {
          ...problem,
          type: problem.type || "about:blank",
          title: problem.title || "Network error",
          status: 0,
          code: problem.code || XONE_ERROR_CODES.BACKEND_NETWORK,
          detail: problem.detail ?? err.message
        },
        err.message
      );
    }
    const msg = String(err);
    return new XoneBackendError(
      XONE_ERROR_CODES.BACKEND_NETWORK,
      0,
      {
        type: "about:blank",
        title: "Unknown error",
        status: 0,
        code: XONE_ERROR_CODES.BACKEND_NETWORK,
        detail: msg
      },
      msg
    );
  }
};

// src/factory/createBackendClient.ts
function createBackendClient(config) {
  if (!config.apiBase) {
    throw new Error(
      "createBackendClient: `apiBase` requerida (macro API_BASE). Si la app aun no ha cargado, lee la macro tras el bootstrap."
    );
  }
  const transport = new BackendTransport({
    apiBase: config.apiBase,
    $http: config.$http,
    macros: config.macros ?? null,
    autoIdempotency: config.autoIdempotency !== false
  });
  return assembleClients(transport, config.$http, config.projectId ?? null);
}
export {
  AuditLogClient,
  AuthenticationClient,
  BackendTransport,
  BusinessRulesClient,
  CollectionClient,
  CryptoClient,
  DataQueryClient,
  DataReplicaClient,
  DeviceRegistryClient,
  FileStorageClient,
  MessagingClient,
  PushNotificationClient,
  SessionMacroClient,
  WebRtcSignalingClient,
  XONE_ERROR_CODES,
  XoneBackendError,
  assembleClients,
  buildHeaders,
  createBackendClient,
  generateRowId,
  parseProblemDetails
};
//# sourceMappingURL=index.js.map